import { Link } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";
import SafeImage from "@/components/base/SafeImage";
import { getSpotFallbackImage } from "@/utils/fallbackImage";
import type { TouristSpot } from "@/services/touristSpotService";

interface SpotDistrict {
  slug: string;
  name: string;
  count: number;
}

interface SpotShowcaseProps {
  spots: TouristSpot[];
  districts: SpotDistrict[];
}

export default function SpotShowcase({ spots, districts }: SpotShowcaseProps) {
  if (spots.length === 0) return null;

  return (
    <section className="border-y border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-[1400px] px-6 py-16 md:py-20">
        <SectionHeading
          eyebrow="Explore the coast"
          title="Places to visit on the Canara coast"
          description="Beaches, temples, waterfalls and heritage spots across South Canara, Udupi and North Canara — with timings, directions and businesses nearby."
          action={{ label: "Browse all locations", to: "/locations" }}
        />

        {districts.length > 0 ? (
          <div className="mt-6 flex flex-wrap gap-2">
            {districts.map((district) => (
              <Link
                key={district.slug}
                to={`/location/${district.slug}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-xs font-semibold text-foreground-700 transition-colors duration-200 hover:border-accent-300 hover:bg-accent-100 hover:text-accent-900"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-600">
                  <i className="ri-map-pin-2-line text-[13px]" />
                </span>
                Spots in {district.name}
                <span className="rounded-full bg-background-200 px-1.5 py-0.5 text-[10px] font-bold text-foreground-600">
                  {district.count}
                </span>
              </Link>
            ))}
          </div>
        ) : null}

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {spots.map((spot) => (
            <article
              key={spot.id}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors duration-300 hover:border-accent-300"
            >
              <Link
                to={`/spot/${spot.slug}`}
                className="relative block h-44 w-full overflow-hidden"
                aria-label={`View ${spot.name}`}
              >
                <SafeImage
                  src={spot.image}
                  fallback={getSpotFallbackImage(spot)}
                  alt={`${spot.name} - ${spot.category} in ${spot.districtName}`}
                  title={`${spot.name} - ${spot.category} in ${spot.districtName}`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-foreground-950">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-camera-lens-line text-[13px]" />
                  </span>
                  {spot.category}
                </span>
              </Link>

              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-heading text-base font-bold leading-snug text-foreground-950">
                  <Link to={`/spot/${spot.slug}`} className="transition-colors hover:text-primary-700">
                    {spot.name}
                  </Link>
                </h3>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-foreground-500">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-map-pin-line text-[13px]" />
                  </span>
                  {spot.districtName}
                </p>
                <p className="mt-3 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
                  {spot.blurb}
                </p>
                <div className="mt-4 flex items-center justify-between gap-2 border-t border-background-200 pt-4">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground-500">
                    <span className="flex h-3.5 w-3.5 items-center justify-center text-secondary-600">
                      <i className="ri-hourglass-line text-[13px]" />
                    </span>
                    {spot.idealDuration}
                  </span>
                  <Link
                    to={`/spot/${spot.slug}`}
                    className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-primary-700 transition-colors hover:text-primary-800"
                  >
                    Explore
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-line text-sm transition-transform duration-200 group-hover:translate-x-0.5" />
                    </span>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}